/**
 * ============================================================================
 * UUID GENERATOR
 * ============================================================================
 *
 * Generates RFC4122 version 4 compliant UUIDs with browser compatibility fallback.
 *
 * Purpose:
 * - Provides unique identifiers for products, entities, etc.
 * - Supports modern browsers with native crypto.randomUUID()
 * - Includes fallback for older browsers without crypto API
 *
 * Usage:
 * import { generateUUID } from '@/lib/uuid.js';
 * const id = generateUUID(); // "550e8400-e29b-41d4-a716-446655440000"
 *
 * Browser Support:
 * - Modern: Uses crypto.randomUUID() (more secure, cryptographically random)
 * - Legacy: Uses Math.random() fallback (less secure but compatible)
 *
 * ============================================================================
 */

/**
 * Generates a UUID (Universally Unique Identifier)
 *
 * @returns {string} - UUID in format "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
 *
 * @example
 * const productId = generateUUID();
 * // Returns: "550e8400-e29b-41d4-a716-446655440000"
 */
export function generateUUID() {
    // ========================================================================
    // MODERN BROWSER: Use native crypto.randomUUID()
    // ========================================================================
    // Supported in: Chrome 92+, Firefox 95+, Safari 15.4+, Edge 92+
    // More secure: Uses cryptographically strong random values

    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    // ========================================================================
    // LEGACY BROWSER: Fallback using Math.random()
    // ========================================================================
    // For browsers that don't support crypto.randomUUID()
    // Uses Math.random() which is less secure but widely supported
    //
    // Pattern: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    // - x: any hexadecimal digit (0-9, a-f)
    // - 4: UUID version 4 indicator
    // - y: one of 8, 9, a, or b (variant bits)

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0; // Random number 0-15
        const v = c === 'x' ? r : (r & 0x3) | 0x8; // y must be 8, 9, a, or b
        return v.toString(16); // Convert to hex
    });
}
